import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGetUserInfo } from '../../domains/user/ui/hooks/useGetUserInfo';
import { useAddBuddyPreference } from '../../domains/findBuddy/ui/hooks/useAddBuddyPreference';
import { getRandomBackgroundColor } from './utils/getRandomBackgroundColor';
import ChooseFanStyleStep from './components/ChooseFanStyleStep';
import SelectBuddyKeywords from './components/SelectBuddyKeywords';
import RegisterSnsAccountStep from './components/RegisterSnsAccountStep';
import LoadingProfileCard from './components/LoadingProfileCard';
import { STEP_ORDER } from './constants/fanStyle';

export interface FanStyle {
  fanCareerLevel: 'newbie' | 'veteran' | undefined;
  stanningStyle: 'all_in' | 'realist' | 'homebody' | 'ghost' | undefined;
  affectionType: 'romantic' | 'maternal' | undefined;
  fantasyType: 'rps' | 'napess' | 'all_rounder' | 'non_rps' | undefined;
  fandomSocialStyle: 'social' | 'solo' | undefined;
}

export interface BuddyKeywords {
  keywords: string[];
  allowAgeGap: number | undefined;
  gender: 'female' | 'male' | 'other' | undefined;
  birthYear: number | undefined;
  locationSido: string;
  locationSigungu: string;
}

export interface SnsAccount {
  twitter: {
    id: string;
    isCheckDuplicate: boolean;
  };
  instagram: {
    id: string;
    isCheckDuplicate: boolean;
  };
}

export type FanBuddySettingStep =
  | 'choose-fan-style'
  | 'select-buddy-keywords'
  | 'register-sns-account'
  | 'loading-profile-card';

const FanBuddySettingPage = () => {
  const { i18n } = useTranslation();
  const isEnLocale = i18n.language?.startsWith('en') ?? false;
  const [currentSettingStep, setCurrentSettingStep] =
    useState<FanBuddySettingStep>('choose-fan-style');
  const [fanStyleStartStep, setFanStyleStartStep] =
    useState<(typeof STEP_ORDER)[number]>('fanCareerLevel');
  const [buddyKeywordsShowAllSteps, setBuddyKeywordsShowAllSteps] = useState(false);
  const [fanStyle, setFanStyle] = useState<FanStyle>({
    fanCareerLevel: undefined,
    stanningStyle: undefined,
    affectionType: undefined,
    fantasyType: undefined,
    fandomSocialStyle: undefined
  });
  const [buddyKeywords, setBuddyKeywords] = useState<BuddyKeywords>({
    keywords: [],
    allowAgeGap: 0,
    gender: undefined,
    birthYear: undefined,
    locationSido: '서울특별시',
    locationSigungu: '종로구'
  });
  const [snsAccounts, setSnsAccounts] = useState<SnsAccount>({
    twitter: {
      id: '',
      isCheckDuplicate: false
    },
    instagram: {
      id: '',
      isCheckDuplicate: false
    }
  });

  const location = useLocation();
  const backgroundColor = new URLSearchParams(location.search).get('backgroundColor');

  const handleSetCurrentSettingStep = (step: FanBuddySettingStep) => {
    if (step === 'select-buddy-keywords') {
      setBuddyKeywordsShowAllSteps(false);
    }
    setCurrentSettingStep(step);
  };

  const handleGoToFanStyleLastStep = () => {
    const lastStep = STEP_ORDER[STEP_ORDER.length - 1] ?? 'fanCareerLevel';
    setFanStyleStartStep(lastStep);
    setCurrentSettingStep('choose-fan-style');
  };

  const handleBackToBuddyKeywordsAllSteps = () => {
    setBuddyKeywordsShowAllSteps(true);
    setCurrentSettingStep('select-buddy-keywords');
  };

  const handleUpdateFanStyle = (newFanStyle: Partial<FanStyle>) => {
    setFanStyle((prev) => ({ ...prev, ...newFanStyle }));
  };

  const handleUpdateBuddyKeywords = (newBuddyKeywords: Partial<BuddyKeywords>) => {
    setBuddyKeywords((prev) => ({ ...prev, ...newBuddyKeywords }));
  };

  const { mutateAsync: addBuddyPreference } = useAddBuddyPreference();
  const { data: user } = useGetUserInfo();

  const userProfileId = user?.baseUserProfile?.userProfileId;

  const handleAddBuddyPreference = async (snsAccountsOverride?: SnsAccount) => {
    if (!userProfileId) return;

    try {
      const effectiveSnsAccounts = snsAccountsOverride ?? snsAccounts;
      const requestBody = {
        user_profile_id: userProfileId,
        buddy_preference: {
          fan_career_level: fanStyle.fanCareerLevel,
          stanning_style: fanStyle.stanningStyle,
          affection_type: fanStyle.affectionType,
          fantasy_type: fanStyle.fantasyType,
          fandom_social_style: fanStyle.fandomSocialStyle,
          keywords: buddyKeywords.keywords,
          allow_age_gap: buddyKeywords.allowAgeGap,
          gender: buddyKeywords.gender,
          birthyear: buddyKeywords.birthYear,
          location_sido: isEnLocale ? '' : buddyKeywords.locationSido,
          location_sigungu: isEnLocale ? '' : buddyKeywords.locationSigungu,
          twitter_id: effectiveSnsAccounts.twitter.id ?? null,
          instagram_id: effectiveSnsAccounts.instagram.id ?? null,
          background_color: backgroundColor
            ? decodeURIComponent(backgroundColor)
            : getRandomBackgroundColor()
        }
      };
      const response = await addBuddyPreference(requestBody);
      if (!response.affectionType) {
        throw new Error('Failed to add buddy preference');
      }
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: API 실패 로그는 개발 중 확인용
      console.error(error);
    }
  };

  switch (currentSettingStep) {
    case 'choose-fan-style':
      return (
        <ChooseFanStyleStep
          handleSettingStep={handleSetCurrentSettingStep}
          onUpdateFanStyle={handleUpdateFanStyle}
          initialStep={fanStyleStartStep}
        />
      );
    case 'select-buddy-keywords':
      return (
        <SelectBuddyKeywords
          handleSettingStep={handleSetCurrentSettingStep}
          buddyKeywords={buddyKeywords}
          onUpdateBuddyKeywords={handleUpdateBuddyKeywords}
          onBackToFanStyleLastStep={handleGoToFanStyleLastStep}
          forceShowAllSteps={buddyKeywordsShowAllSteps}
        />
      );
    case 'register-sns-account':
      return (
        <RegisterSnsAccountStep
          handleSettingStep={handleSetCurrentSettingStep}
          snsAccounts={snsAccounts}
          onUpdateSnsAccounts={setSnsAccounts}
          onAddBuddyPreference={handleAddBuddyPreference}
          onBackToBuddyKeywordsAllSteps={handleBackToBuddyKeywordsAllSteps}
        />
      );
    case 'loading-profile-card':
      return <LoadingProfileCard />;
  }
};

export default FanBuddySettingPage;
